import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { fi } from 'date-fns/locale';
import { format, isValid } from 'date-fns';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { PICKER_MINUTES } from '../calendar/calendarConstants';

// PASI and partner bookings are placed from their own reservation records; the backend
// refuses a time edit for them (error_code not_editable_source), so the fields are hidden.
const LOCKED_SOURCES = ['pasi', 'partner'];

const toDate = (dateStr) => (dateStr ? new Date(`${dateStr}T00:00:00`) : null);
const toTime = (timeStr) => {
  if (!timeStr) return null;
  const [hours, minutes] = timeStr.split(':').map(Number);
  const d = new Date();
  d.setHours(hours, minutes, 0, 0);
  return d;
};
const dateToStr = (d) => (d && isValid(d) ? format(d, 'yyyy-MM-dd') : '');
const timeToStr = (d) => (d && isValid(d) ? format(d, 'HH:mm') : '');
const fmtDate = (dateStr) => (dateStr ? toDate(dateStr).toLocaleDateString('fi-FI') : '');
const fmtTime = (timeStr) => (timeStr ? timeStr.substring(0, 5) : '');

/**
 * "Vahvista varaus" for a pending online booking.
 *
 * Opens prefilled with exactly what the customer reserved. Untouched, it confirms as before
 * (POST with no body). When the workshop picks another date/time the body carries it, the
 * work order lands on that slot and the one confirmation message the customer gets shows
 * the final time. The slot check only warns; it never blocks.
 */
export default function BookingConfirmDialog({ booking, open, onClose, onActionComplete }) {
  const { t } = useTranslation();
  const editable = !!booking && !LOCKED_SOURCES.includes(booking.source);
  const isDropOff = booking?.booking_mode === 'drop_off';

  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [loading, setLoading] = useState(false);
  const [slotCheck, setSlotCheck] = useState(null);
  const checkTimer = useRef(null);

  // Prefill on open. Keyed on the id, not the object: a list refetch hands in a new object
  // for the same booking and must not wipe what the user has typed.
  const bookingId = booking?.id;
  useEffect(() => {
    if (open && booking) {
      setDate(toDate(booking.booking_date));
      setTime(toTime(booking.booking_time));
      setSlotCheck(null);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bookingId]);

  const dateStr = dateToStr(date);
  const timeStr = timeToStr(time);
  const dateValid = !!dateStr;
  const timeValid = isDropOff || !!timeStr;
  const changed =
    editable &&
    !!booking &&
    (dateStr !== booking.booking_date || (!isDropOff && timeStr !== fmtTime(booking.booking_time)));

  // Warn-only conflict check, debounced, for time-slot bookings the workshop can move.
  // Keyed on the id like the prefill: a refetched booking object must not re-fire it.
  useEffect(() => {
    if (!open || !bookingId || !editable || isDropOff || !changed || !dateValid || !timeValid) {
      setSlotCheck(null);
      return undefined;
    }
    clearTimeout(checkTimer.current);
    checkTimer.current = setTimeout(async () => {
      try {
        const response = await axios.get(`/api/bookings/${bookingId}/slot-check/`, {
          params: { booking_date: dateStr, booking_time: timeStr },
        });
        setSlotCheck(response.data);
      } catch (err) {
        setSlotCheck(null);
      }
    }, 300);
    return () => clearTimeout(checkTimer.current);
  }, [open, bookingId, editable, isDropOff, changed, dateValid, timeValid, dateStr, timeStr]);

  const handleConfirm = async () => {
    if (!booking) return;
    setLoading(true);
    try {
      if (changed) {
        const body = { booking_date: dateStr };
        if (!isDropOff) body.booking_time = timeStr;
        await axios.post(`/api/bookings/${booking.id}/confirm/`, body);
      } else {
        await axios.post(`/api/bookings/${booking.id}/confirm/`);
      }
      onActionComplete(t('booking.actions.confirmed'), 'success');
      onClose();
    } catch (err) {
      console.error('Booking confirm failed:', err);
      const code = err.response?.data?.error_code;
      const known = ['date_in_past', 'time_required', 'not_editable_source', 'invalid', 'not_pending'];
      const message = known.includes(code)
        ? t(`booking.confirm.errors.${code}`)
        : err.response?.data?.error || t('booking.confirm.errors.generic');
      onActionComplete(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const warnings = [];
  if (slotCheck) {
    (slotCheck.overlapping_schedules || []).forEach((s) => {
      warnings.push(
        t('booking.confirm.conflict_schedule', {
          registration: s.registration || '-',
          start: s.start,
          end: s.end,
        })
      );
    });
    const busy = (slotCheck.overlapping_bookings || 0) + (slotCheck.overlapping_schedules || []).length;
    if (slotCheck.concurrent_slots && busy >= slotCheck.concurrent_slots) {
      warnings.push(t('booking.confirm.conflict_capacity', { count: slotCheck.concurrent_slots }));
    }
    if (slotCheck.resources_free === false) {
      warnings.push(t('booking.confirm.conflict_resources'));
    }
    if (slotCheck.outside_booking_hours) {
      warnings.push(t('booking.confirm.conflict_hours'));
    }
  }

  const canSubmit = !loading && (!changed || (dateValid && timeValid));

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="xs" fullWidth data-testid="booking-confirm-dialog">
      <DialogTitle>{t('booking.actions.confirm_title')}</DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: editable ? 2 : 0 }}>{t('booking.actions.confirm_message')}</Typography>

        {editable && (
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fi}>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <DatePicker
                label={t('booking.confirm.date')}
                value={date}
                onChange={setDate}
                format="dd.MM.yyyy"
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { flex: '1 1 160px' },
                    inputProps: { 'data-testid': 'booking-confirm-date' },
                  },
                }}
              />
              {!isDropOff && (
                <TimePicker
                  label={t('booking.confirm.time')}
                  value={time}
                  onChange={setTime}
                  ampm={false}
                  minutesStep={PICKER_MINUTES}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { flex: '1 1 120px' },
                      inputProps: { 'data-testid': 'booking-confirm-time' },
                    },
                  }}
                />
              )}
            </Box>
          </LocalizationProvider>
        )}

        {changed && (
          <Alert severity="info" sx={{ mt: 2, borderRadius: '8px' }} data-testid="booking-confirm-requested">
            {isDropOff
              ? t('booking.confirm.requested_line_dropoff', { date: fmtDate(booking.booking_date) })
              : t('booking.confirm.requested_line', {
                  date: fmtDate(booking.booking_date),
                  time: fmtTime(booking.booking_time),
                })}
          </Alert>
        )}

        {warnings.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2, borderRadius: '8px' }} data-testid="booking-confirm-conflicts">
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {t('booking.confirm.conflicts_title')}
            </Typography>
            {warnings.map((line) => (
              <Typography key={line} variant="body2">
                {line}
              </Typography>
            ))}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t('Cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          disabled={!canSubmit}
          data-testid="booking-confirm-submit"
        >
          {loading ? <CircularProgress size={20} /> : t('booking.actions.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
